import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Box, OrgChart, Separator, Skeleton, Text } from "@salestrak/ui";

import { PageHeader } from "#app/components/page-header";
import { useEntityDetail } from "#app/components/entity";
import { RequireMemberBrowse } from "#app/components/permissions/require-permission";
import { MemberJumpSearch } from "#app/components/org-chart/member-jump-search";
import { OrgChartPersonContent } from "#app/components/org-chart/org-chart-person-content";
import {
  OrgChartViewOptionsMenu,
  orgChartNodeHeight,
  useOrgChartViewOptions,
} from "#app/components/org-chart/org-chart-view-options";
import {
  useFullOrgChart,
  type OrgChartUserItem,
} from "#app/components/org-chart/use-org-chart-data";

/**
 * The Teams module's workspace-wide org chart. Reporting lines come from
 * user.managerId; pressing a node opens (or retargets) the profile island,
 * and the island's "Explore" banner deep-links back here with ?focus=<id>.
 */
function OrgChartScreen() {
  const { items, defaultCollapsedIds, ready } = useFullOrgChart();
  const { openEntity } = useEntityDetail();
  const { focus } = Route.useSearch();

  // "Jump to member" search — the ONE find-target concept on this canvas
  // (ring + centering). A profile-Explore deep link (?focus=) just seeds the
  // search, so the target is always visible in the field and clearable via
  // its × — no invisible URL state keeping a stale ring alive.
  const [jumpId, setJumpId] = useState<string | null>(focus ?? null);
  useEffect(() => {
    if (focus) setJumpId(focus);
  }, [focus]);

  const viewOptions = useOrgChartViewOptions();
  const { showTitle, showRole } = viewOptions.options;
  // The skeleton mirrors the settled cards, so it stands on the same rung.
  const nodeHeight = orgChartNodeHeight(viewOptions.options);
  const renderNode = useCallback(
    (item: OrgChartUserItem) =>
      item.data.kind === "placeholder" ? (
        // Ink comes from the placeholder pill — the library's solid tile
        // sets white ink in both modes; a themed ink here would fight the fill.
        <Text size="sm">No manager</Text>
      ) : (
        <OrgChartPersonContent
          showRole={showRole}
          showTitle={showTitle}
          user={item.data.user}
        />
      ),
    [showRole, showTitle],
  );
  const handleNodePress = useCallback(
    (id: string) => openEntity("user", id, { via: "org_chart" }),
    [openEntity],
  );

  return (
    <Box direction="column" fill minHeight={0}>
      <PageHeader
        title="Org Chart"
        actions={
          <>
            <MemberJumpSearch
              items={items}
              onValueChange={setJumpId}
              value={jumpId}
            />
            <OrgChartViewOptionsMenu {...viewOptions} />
          </>
        }
      />
      <Separator />
      {!ready ? (
        // The charted pool is still assembling (a workspace always has at
        // least the signed-in member, so the chart is never legitimately
        // empty) — sketch a small indented tree while it arrives.
        <Box fill padding="xl">
          <Box direction="column" gap="md">
            <Skeleton height={nodeHeight} width={224} />
            <Box paddingLeft="xl">
              <Skeleton height={nodeHeight} width={224} />
            </Box>
            <Box paddingLeft="xl">
              <Box direction="column" gap="md" paddingLeft="xl">
                <Skeleton height={nodeHeight} width={224} />
                <Skeleton height={nodeHeight} width={224} />
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <OrgChart
          defaultCollapsedIds={defaultCollapsedIds}
          expandable
          // Pressing a node opens the profile island without becoming
          // "active" — only the search target rings.
          focusId={jumpId}
          items={items}
          minimap
          nodeHeight={nodeHeight}
          onNodePress={handleNodePress}
          renderNode={renderNode}
          selectedId={jumpId}
        />
      )}
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/org-chart")({
  validateSearch: (search: Record<string, unknown>): { focus?: string } =>
    typeof search.focus === "string" && search.focus.length > 0
      ? { focus: search.focus }
      : {},
  component: OrgChartRoute,
});

// The chart is a member surface: a share-only viewer is denied (see
// RequireMemberBrowse) — the roster is not shared work.
function OrgChartRoute() {
  return (
    <RequireMemberBrowse>
      <OrgChartScreen />
    </RequireMemberBrowse>
  );
}
